import {
  Flex,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Spinner,
  Text,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Files } from "../../services/FilesService";
import { PlanTemplateService } from "../../services/PlanTemplateService";
import { CANVAS, EXTERNAL } from "../Assessment/Plan/utils";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { EMPTY_RTE } from "../CustomTextArea/CustomRTE";
import { DialogYesNo } from "../DialogYesNo/DialogYesNo";

const TemplateCard = ({ template, setTemplates, setSelectedTemplate }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);

  async function deleteAssignmentDescriptions() {
    try {
      const deletePromises = [];
      template.sections.forEach((section) => {
        section.measures.forEach((measure) => {
          if (measure.description_key) {
            deletePromises.push(
              Files.deleteFile(
                "response.html",
                "templateAssignmentDescription",
                measure.description_key
              )
            );
          }
        });
      });
      await Promise.all(deletePromises);
    } catch {
      throw new Error("Failed to delete assignment descriptions");
    }
  }

  async function handleDelete() {
    try {
      await deleteAssignmentDescriptions();
      await Files.deleteFile("response.html", "templateHeader", template.id);
      await PlanTemplateService.deleteTemplate(template.id);
      setTemplates((prevTemplates) =>
        prevTemplates.filter((t) => t.id !== template.id)
      );
      setDeleteModal(false);
      showNotification(
        t("notifications.general.deleteSuccess", {
          obj: t("planTemplates.template"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", {
          obj: t("planTemplates.template"),
        }),
        ERROR
      );
    }
  }

  async function handleEditTemplate(template) {
    try {
      setLoading(true);
      const updatedSections = await Promise.all(
        template.sections.map(async (section) => {
          const updatedMeasures = await Promise.all(
            section.measures.map(async (measure) => {
              if (measure.type === CANVAS || measure.type === EXTERNAL) {
                let descriptionText;
                if (measure.description_key) {
                  const description = await Files.getFileContent(
                    "response.html",
                    "templateAssignmentDescription",
                    measure.description_key
                  );
                  descriptionText = description.content;
                } else {
                  descriptionText = measure.description || EMPTY_RTE;
                }
                const updatedMeasure = {
                  ...measure,
                  description_text: descriptionText,
                };
                delete updatedMeasure.description;
                return updatedMeasure;
              }
              return measure;
            })
          );
          return { ...section, measures: updatedMeasures };
        })
      );
      const headerRes = await Files.getFileContent(
        "response.html",
        "templateHeader",
        template.id
      );
      const headerContent = headerRes.content;

      setSelectedTemplate({
        ...template,
        sections: updatedSections,
        headerContent,
      });
    } catch {
      showNotification(
        t("notifications.general.fetchError", {
          obj: t("planTemplates.template"),
        }),
        ERROR
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card
      style={{
        color: "#0D323F",
        backgroundColor: "#F2F8FA",
        border: "1px solid #ccc",
        borderRadius: 10,
        padding: "10px 15px",
        marginBottom: 15,
      }}
    >
      <Flex>
        <Flex.Item shouldGrow shouldShrink>
          <Text weight="bold">{template.name}</Text>
        </Flex.Item>
        <Flex.Item margin="0 small 0 0">
          <IconButton
            renderIcon={
              loading ? (
                <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
              ) : (
                IconEditLine
              )
            }
            screenReaderLabel={t("templates.editTemplate")}
            size="small"
            disabled={loading}
            onClick={() => handleEditTemplate(template)}
          />
        </Flex.Item>
        <Flex.Item>
          <IconButton
            renderIcon={IconTrashLine}
            screenReaderLabel={t("templates.deleteTemplate")}
            size="small"
            onClick={() => setDeleteModal(true)}
          />
        </Flex.Item>
      </Flex>
      <DialogYesNo
        message={t("templates.deleteConfirm")}
        openStatus={deleteModal}
        onClickNo={() => setDeleteModal(false)}
        onClickYes={handleDelete}
      />
    </Card>
  );
};

export default TemplateCard;
